import React from 'react';
import {
  Card,
  CardContent,
  Box,
  Typography,
  IconButton,
  Chip,
  Collapse,
  Divider,
  Tooltip
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';

interface Requirement {
  id: string;
  topic: string;
  hours: number;
  description?: string;
  frequency?: number;
  interval?: string;
  statute?: string;
  notes?: string;
  isCreditType?: boolean;
  lastUpdated?: string;
}

interface PublicLicensingBoardCardProps {
  id: string;
  name: string;
  generalCMEHours: string | number;
  topicsCount: number;
  licensurePeriod: string;
  website?: string;
  status?: 'active' | 'archived';
  requirements: Requirement[];
  isExpanded?: boolean;
  onExpand?: (id: string) => void;
}

const PublicLicensingBoardCard: React.FC<PublicLicensingBoardCardProps> = ({
  id,
  name,
  generalCMEHours,
  topicsCount,
  licensurePeriod,
  website,
  status = 'active',
  requirements,
  isExpanded = false,
  onExpand
}) => {
  const generalCMEReq = requirements.find(r =>
    r.topic?.toLowerCase() === 'general cme' || r.topic?.toLowerCase() === 'none'
  );
  const hasExpandableContent = !!generalCMEReq?.description;

  const toDisplayHtml = (text: string): string => {
    if (!text) return '';
    if (/<[a-z][\s\S]*>/i.test(text)) return text;
    return text.split('\n').map(line => `<p>${line || '<br>'}</p>`).join('');
  };

  const handleExpandClick = () => {
    if (onExpand && hasExpandableContent) {
      onExpand(id);
    }
  };

  const getDisplayUrl = (url: string): string => {
    try {
      return new URL(url).hostname.replace('www.', '');
    } catch {
      return url.replace(/^https?:\/\//i, '').replace(/^www\./i, '').split('/')[0];
    }
  };

  const formatUrl = (url: string): string => {
    if (!url) return '';
    if (url.startsWith('http://') || url.startsWith('https://')) return url;
    return `https://${url}`;
  };

  const getTopicColor = (topic: string, isCreditType?: boolean): { bg: string; text: string } => {
    const topicLower = topic.toLowerCase();
    if (topicLower.includes('general cme')) return { bg: '#FFE5CC', text: '#E65100' };
    if (isCreditType === true) return { bg: '#C8E6C9', text: '#2E7D32' };
    return { bg: '#E3F2FD', text: '#1565C0' };
  };

  return (
    <Card
      sx={{
        borderRadius: '4px',
        boxShadow: 'none',
        marginBottom: '12px',
        backgroundColor: '#FFFFFF',
        border: '1px solid #E0E0E0',
        transition: 'box-shadow 0.2s ease',
        '&:hover': { boxShadow: '0px 2px 10px rgba(0, 0, 0, 0.15)' }
      }}
    >
      <CardContent sx={{ padding: '16px 20px', '&:last-child': { paddingBottom: '16px' } }}>
        {/* Main Data Row */}
        <Box
          className="licensing-board-row"
          tabIndex={0}
          sx={{
            display: 'grid',
            gridTemplateColumns: {
              xs: '3fr 0.8fr 0.8fr 1fr 1.6fr',
              md: '3fr 0.8fr 0.8fr 1fr 1.6fr 60px'
            },
            gap: '16px',
            alignItems: 'center',
            marginBottom: '12px',
            position: 'relative',
            '&:focus': { outline: 'none' }
          }}
        >
          {/* Licensing Board Name */}
          <Box>
            <Typography
              sx={{
                fontStyle: 'normal',
                fontWeight: 600,
                fontSize: '18px',
                color: '#09101D',
                lineHeight: 1.4,
                textAlign: 'left'
              }}
            >
              {name}
            </Typography>
          </Box>

          {/* General CME */}
          <Box>
            <Typography sx={{ fontWeight: 600, fontSize: '15px', color: '#09101D' }}>
              {generalCMEHours}
            </Typography>
          </Box>

          {/* # of Topics */}
          <Box>
            <Typography sx={{ fontWeight: 600, fontSize: '15px', color: '#09101D' }}>
              {topicsCount === 0 ? '—' : topicsCount}
            </Typography>
          </Box>

          {/* Licensure Period */}
          <Box>
            <Typography sx={{ fontWeight: 600, fontSize: '15px', color: '#09101D' }}>
              {licensurePeriod}
            </Typography>
          </Box>

          {/* Website */}
          <Box sx={{ textAlign: 'left', overflow: 'hidden' }}>
            {website ? (
              <Tooltip title={formatUrl(website)} arrow placement="top">
                <a
                  href={formatUrl(website)}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{
                    color: '#1976D2',
                    textDecoration: 'none',
                    fontSize: '15px',
                    fontWeight: 500,
                    display: 'block',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    maxWidth: '100%'
                  }}
                >
                  {getDisplayUrl(website)}
                </a>
              </Tooltip>
            ) : (
              <Typography sx={{ fontSize: '15px', color: '#999', textAlign: 'left' }}>—</Typography>
            )}
          </Box>

          {/* Expand Icon Only */}
          <Box
            className="licensing-board-action-container"
            sx={{
              display: 'flex',
              justifyContent: 'flex-end',
              position: { xs: 'absolute', md: 'static' },
              right: { xs: '10px', md: 'auto' },
              top: { xs: '50%', md: 'auto' },
              transform: { xs: 'translateY(-50%)', md: 'none' },
              zIndex: 2
            }}
          >
            <IconButton
              size="small"
              onClick={handleExpandClick}
              disabled={!hasExpandableContent}
              className="licensing-board-action-icon"
              sx={{
                padding: '6px',
                color: hasExpandableContent ? '#FFFFFF !important' : '#BBBBBB !important',
                backgroundColor: hasExpandableContent ? '#757575 !important' : '#F0F0F0 !important',
                border: 'none !important',
                '&:hover': {
                  backgroundColor: hasExpandableContent ? '#616161 !important' : '#F0F0F0 !important'
                }
              }}
            >
              {isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            </IconButton>
          </Box>
        </Box>

        {/* Requirement Badges */}
        <Box sx={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginTop: '12px' }}>
          {[...requirements]
            .filter((req) => !(req.topic.toLowerCase() === 'general cme' && req.hours === 0))
            .sort((a, b) => {
              const aTopic = a.topic.toLowerCase();
              const bTopic = b.topic.toLowerCase();
              const aIsGeneral = aTopic.includes('general cme');
              const bIsGeneral = bTopic.includes('general cme');
              if (aIsGeneral && !bIsGeneral) return -1;
              if (!aIsGeneral && bIsGeneral) return 1;
              if (a.isCreditType && !b.isCreditType) return -1;
              if (!a.isCreditType && b.isCreditType) return 1;
              return 0;
            })
            .map((req) => {
              const colors = getTopicColor(req.topic, req.isCreditType);
              return (
                <Chip
                  key={req.id}
                  label={
                    <Box sx={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
                      <span>{req.topic}</span>
                      <span
                        style={{
                          fontWeight: 600,
                          backgroundColor: '#FFFFFF',
                          padding: '2px 6px',
                          borderRadius: '8px'
                        }}
                      >
                        {req.hours}
                      </span>
                    </Box>
                  }
                  sx={{
                    backgroundColor: colors.bg,
                    color: colors.text,
                    fontSize: '13px',
                    fontWeight: 500,
                    height: '26px',
                    borderRadius: '13px',
                    '& .MuiChip-label': { padding: '0 10px' }
                  }}
                />
              );
            })}
        </Box>

        {/* Expanded Details */}
        <Collapse in={isExpanded} timeout="auto" unmountOnExit>
          <Box sx={{ marginTop: '20px', width: '100%' }}>
            <Divider sx={{ marginBottom: '20px' }} />
            {generalCMEReq?.description && (
              <Box
                sx={{
                  marginBottom: '14px',
                  width: '100%',
                  fontSize: '15px',
                  lineHeight: 1.6,
                  color: '#333333',
                  fontWeight: 400,
                  textAlign: 'left',
                  fontFamily: 'inherit',
                  '& p': { margin: '0 0 8px 0' },
                  '& ul, & ol': { paddingLeft: '20px', margin: '0 0 8px 0' },
                  '& strong': { fontWeight: 700 },
                  '& em': { fontStyle: 'italic' },
                  '& u': { textDecoration: 'underline' },
                  '& hr': { border: 'none', borderTop: '1px solid #E0E0E0', margin: '12px 0' }
                }}
                dangerouslySetInnerHTML={{ __html: toDisplayHtml(generalCMEReq.description!) }}
              />
            )}
            {generalCMEReq?.lastUpdated && (
              <Typography
                sx={{
                  fontSize: '12px',
                  color: '#666',
                  marginTop: '12px',
                  fontStyle: 'italic',
                  textAlign: 'left'
                }}
              >
                Last updated {generalCMEReq.lastUpdated}
              </Typography>
            )}
          </Box>
        </Collapse>
      </CardContent>
    </Card>
  );
};

export default PublicLicensingBoardCard;
